import { getServiceField } from "pages/future-medical/shared/future-medical-utils";
import { useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import {
  setCurrentServiceDoi,
  setSelectedServiceDoi,
} from "reducers/future-medical";
import {
  useGetDateOfInjuryByIdQuery,
  useGetServiceDateOfInjuryQuery,
} from "services/doi-api";
import type {
  CostProjectionDataType,
  DateOfInjury,
  DocumentManagementDataType,
  MSADataType,
  ServiceDateOfInjury,
} from "types/DataTypes";
import { useSelector } from "utils/redux";

// fetch DOIs for a service
function useServiceDOIsQuery(
  service?: MSADataType | CostProjectionDataType | DocumentManagementDataType,
) {
  const serviceParams = service ? getServiceField(service, true) : undefined;
  const serviceId = service?.id;

  // fetch all service date of injuries for this service
  const result = useGetServiceDateOfInjuryQuery(
    {
      ...serviceParams,
    },
    {
      skip: !serviceId,
    },
  );

  return {
    ...result,
    data: result.data?.results,
    serviceId,
  };
}

// fetch a specific DOI by ID
export function useDateOfInjury(doiId?: number) {
  return useGetDateOfInjuryByIdQuery(
    {
      id: doiId,
    },
    {
      skip: !doiId,
    },
  );
}

// handle selection state for a service DOI
function useServiceDOISelection(
  serviceDOIs?: ServiceDateOfInjury[],
  serviceId?: number,
) {
  const dispatch = useDispatch();
  const hasAutoSelected = useRef(false);

  // get selected DOI from Redux
  const selectedDoiId = useSelector(
    (state) => state.futureMedical.selectedServiceDoi,
  );

  // find the selected DOI in the serviceDOIs array
  const selectedDoi =
    serviceDOIs?.find((doi) => doi.id === selectedDoiId) || null;

  // reset selection tracking when service changes
  useEffect(() => {
    if (!serviceId) return;

    // reset selection state when service changes
    dispatch(setSelectedServiceDoi(null));
    dispatch(setCurrentServiceDoi(null));
    hasAutoSelected.current = false;
  }, [dispatch, serviceId]);

  // select a DOI by ID
  const selectDoi = useCallback(
    (doiId: number | string | null) => {
      if (!serviceDOIs?.length) return;

      const numericId = doiId
        ? typeof doiId === "string"
          ? parseInt(doiId)
          : doiId
        : null;

      dispatch(setSelectedServiceDoi(numericId));

      // update currentServiceDoi if we have data
      const selectedDoiData = numericId
        ? serviceDOIs.find((doi) => doi.id === numericId)
        : null;

      dispatch(setCurrentServiceDoi(selectedDoiData || null));
    },
    [serviceDOIs, dispatch],
  );

  // auto-select first DOI when data loads
  useEffect(() => {
    if (
      !hasAutoSelected.current &&
      serviceDOIs?.length &&
      !selectedDoiId &&
      serviceId
    ) {
      selectDoi(serviceDOIs[0].id);
      hasAutoSelected.current = true;
    }
  }, [serviceDOIs, selectedDoiId, serviceId, selectDoi]);

  return {
    selectedDoiId,
    selectedDoi,
    selectDoi,
  };
}

type UseServiceDOIResult = {
  serviceDOIs: ServiceDateOfInjury[] | undefined;
  selectedServiceDoi: ServiceDateOfInjury | null;
  selectedServiceDoiId: number | null;
  selectedDOI: DateOfInjury | undefined;
  selectDoi: (doiId: number | string | null) => void;
  getDoiById: (id: number) => ServiceDateOfInjury | null;
  isLoading: boolean;
  isSuccess: boolean;
};

/**
 * handle service-based DOI selection and data fetching
 * @param service MSA or Cost Projection service
 */
export function useServiceDOI(
  service?: MSADataType | CostProjectionDataType | DocumentManagementDataType,
): UseServiceDOIResult {
  // fetch service DOIs for the service
  const {
    data: serviceDOIs,
    isLoading,
    isSuccess,
    serviceId,
  } = useServiceDOIsQuery(service);

  // handle DOI selection
  const {
    selectedDoiId: selectedServiceDoiId,
    selectedDoi: selectedServiceDoi,
    selectDoi,
  } = useServiceDOISelection(serviceDOIs, serviceId);

  // fetch the selected DOI data
  const { data: selectedDOI } = useDateOfInjury(
    selectedServiceDoi?.date_of_injury_id,
  );

  // utility to get a specific DOI by ID
  const getDoiById = useCallback(
    (id: number) => {
      if (!serviceDOIs) return null;
      return serviceDOIs.find((doi) => doi.id === id) || null;
    },
    [serviceDOIs],
  );

  return {
    serviceDOIs,
    selectedServiceDoi,
    selectedServiceDoiId,
    selectedDOI,
    selectDoi,
    getDoiById,
    isLoading,
    isSuccess,
  };
}
